import React, { useState, useMemo } from "react";

import DashboardView from "./DashboardView";

// -------------------- Mock Data --------------------

const mockRetailers = [
  {
    id: 1,
    name: "Shree Mobile Store",
    code: "RT001",
    city: "Pune",
    isActive: true,
    isConnected: true,
    usingProduct: true,
    phone: "+91 9876543210",
    email: "owner@shreemobile.in",
    contactPerson: "Rahul Patil",
    gstin: "27ABCDE1234F1Z5",
    address: "Shop 12, Main Market, Pune",
    totalSales: 450000,
    monthSales: 75000,
    outstanding: 15000,
    lastPaymentDate: "2025-11-01",
    productMix: [
      { product: "POS License", value: 25000 },
      { product: "Recharge API", value: 30000 },
      { product: "DMT", value: 20000 },
    ],
  },
  {
    id: 2,
    name: "Sai Telecom",
    code: "RT002",
    city: "Mumbai",
    isActive: true,
    isConnected: false,
    usingProduct: false,
    phone: "+91 9822001122",
    email: "sai@telecom.com",
    contactPerson: "Sanjay More",
    gstin: "27PQRSX6789L1Z8",
    address: "G-5, Station Road, Mumbai",
    totalSales: 120000,
    monthSales: 12000,
    outstanding: 5000,
    lastPaymentDate: "2025-10-25",
    productMix: [
      { product: "POS License", value: 60000 },
      { product: "Recharge API", value: 30000 },
      { product: "AEPS", value: 30000 },
    ],
  },
  {
    id: 3,
    name: "Om Electronics",
    code: "RT003",
    city: "Nashik",
    isActive: false,
    isConnected: false,
    usingProduct: false,
    phone: "+91 9988776655",
    email: "info@omelectronics.in",
    contactPerson: "Prakash Jadhav",
    gstin: "27XYZAB1357K1Z9",
    address: "Shop 3, City Center, Nashik",
    totalSales: 80000,
    monthSales: 0,
    outstanding: 0,
    lastPaymentDate: "2025-08-10",
    productMix: [
      { product: "POS License", value: 30000 },
      { product: "Recharge API", value: 20000 },
      { product: "DMT", value: 30000 },
    ],
  },
];

const mockTasks = [
  {
    id: 101,
    retailerId: 1,
    title: "Follow up for November collection",
    description: "Check if payment for November is processed.",
    status: "In Progress", // 'Open' | 'In Progress' | 'Done'
    priority: "High",
    dueDate: "2025-11-18",
    type: "Collection", // Call / Visit / Training / Collection
  },
  {
    id: 102,
    retailerId: 2,
    title: "Connect retailer to platform",
    description: "Help them onboard to new dashboard.",
    status: "Open",
    priority: "Medium",
    dueDate: "2025-11-20",
    type: "Training",
  },
  {
    id: 103,
    retailerId: 3,
    title: "Win-back inactive retailer",
    description: "Call and understand reason for inactivity.",
    status: "Open",
    priority: "Low",
    dueDate: "2025-11-25",
    type: "Call",
  },
];

const mockSalesTrend = [
  { month: "Jun", sales: 120000, collection: 110000 },
  { month: "Jul", sales: 150000, collection: 130000 },
  { month: "Aug", sales: 180000, collection: 170000 },
  { month: "Sep", sales: 210000, collection: 200000 },
  { month: "Oct", sales: 240000, collection: 220000 },
  { month: "Nov", sales: 250000, collection: 230000 },
];

function OperationDashboard() {
  const [retailers, setRetailers] = useState(mockRetailers);
  const [tasks, setTasks] = useState(mockTasks);
  const [currentView, setCurrentView] = useState("dashboard"); // 'dashboard' | 'retailers' | 'tasks' | 'reports'
  const [selectedRetailerId, setSelectedRetailerId] = useState(
    mockRetailers[0].id
  );
  const [searchRetailer, setSearchRetailer] = useState("");

  const selectedRetailer = useMemo(
    () => retailers.find((r) => r.id === selectedRetailerId),
    [retailers, selectedRetailerId]
  );

  const [initialTaskFilter, setInitialTaskFilter] = useState("All");

  // --------------- KPI Calculations ---------------

  const kpis = useMemo(() => {
    const totalAssigned = retailers.length;
    const active = retailers.filter((r) => r.isActive).length;
    const inactive = totalAssigned - active;
    const connected = retailers.filter((r) => r.isConnected).length;
    const notConnected = totalAssigned - connected;
    const usingProduct = retailers.filter((r) => r.usingProduct).length;
    const notUsingProduct = totalAssigned - usingProduct;

    const totalSales = retailers.reduce((sum, r) => sum + r.totalSales, 0);
    const monthSales = retailers.reduce((sum, r) => sum + r.monthSales, 0);
    const outstanding = retailers.reduce((sum, r) => sum + r.outstanding, 0);

    const totalTasks = tasks.length;
    const doneTasks = tasks.filter((t) => t.status === "Done").length;
    const openTasks = totalTasks - doneTasks;

    return {
      totalAssigned,
      active,
      inactive,
      connected,
      notConnected,
      usingProduct,
      notUsingProduct,
      totalSales,
      monthSales,
      outstanding,
      totalTasks,
      doneTasks,
      openTasks,
    };
  }, [retailers, tasks]);

  // --------------- Task Handlers ---------------

  const [newTask, setNewTask] = useState({
    retailerId: "",
    title: "",
    description: "",
    dueDate: "",
    priority: "Medium",
    type: "Call",
  });

  const handleCreateTask = (e) => {
    e.preventDefault();
    if (!newTask.title || !newTask.retailerId) return;

    const newTaskObj = {
      id: Date.now(),
      retailerId: Number(newTask.retailerId),
      title: newTask.title,
      description: newTask.description,
      status: "Open",
      priority: newTask.priority,
      dueDate: newTask.dueDate || "",
      type: newTask.type,
    };

    setTasks((prev) => [newTaskObj, ...prev]);
    setNewTask({
      retailerId: "",
      title: "",
      description: "",
      dueDate: "",
      priority: "Medium",
      type: "Call",
    });
  };

  const updateTaskStatus = (id, status) => {
    setTasks((prev) => prev.map((t) => (t.id === id ? { ...t, status } : t)));
  };

  // --------------- Filtered Retailers ---------------

  const filteredRetailers = useMemo(() => {
    if (!searchRetailer.trim()) return retailers;
    const q = searchRetailer.toLowerCase();
    return retailers.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.code.toLowerCase().includes(q) ||
        r.city.toLowerCase().includes(q) ||
        r.gstin.toLowerCase().includes(q)
    );
  }, [retailers, searchRetailer]);

  // -------------------- VIEWS --------------------

  return (
    // <div className="min-h-screen bg-slate-100 flex text-slate-900">
      
      <div className="flex-1 flex flex-col">
        
        <main className="flex-1 p-4 md:p-6">
          {currentView === "dashboard" && (
            <DashboardView
              kpis={kpis}
              salesTrend={mockSalesTrend}
              retailers={retailers}
              tasks={tasks}
              setCurrentView={setCurrentView}
              setInitialTaskFilter={setInitialTaskFilter}   // 👈 added
            />

          )}
        </main>
      </div>
    // </div>
  );
}

export default OperationDashboard;
